<template>
  <div class="suggest">
    <div class="suggest__wrapper">
      <TextInput
        v-model="query"
        :type="type"
        :name="name"
        :label="label"
        :focus="focus"
        :hint="hint"
        :error="error"
        :chip="chip"
        :inverse="inverse"
        :hasFocus="hasFocus"
        @input="filterByQuery"
        @focus="onFocus"
        @blur="onBlur"
        @arrow-move="arrowMoved"
        @dropdown-select="selectDropdown"
        @remove-chip="selectResult(null)"
        icon="search"
        noSpacing
        hideHintError
        :disabled="disabled"
        ref="input"
        :dropdown="query.length > 0 || showOnClick"
      />
      <DropdownList
        v-if="!disabled && !isAlgolia && (showOnClick || query.length > 0)"
        :items="queryItems"
        :index="currentIndex"
        :photoField="photoField"
        :titleField="titleField"
        :descField="descField"
        :value="value"
        :create="createText"
        @create="createNewResult"
        @update-index="currentIndex = $event"
        @select="selectResult"
        @mouseover="mouseover($event)"
        :isPremise="isPremise"
        ref="dropdown"
      />
    </div>
    <InputHint
      :error="error"
      :hint="hint"
      :required="required"
      :showOptional="showOptional"
    />
  </div>
</template>

<script>
import { mapState } from "vuex";
import DropdownList from "./DropdownList";
import InputHint from "./InputHint";
import TextInput from "./TextInput";
import { createFromSerialized } from "vue-instantsearch";

export default {
  name: "Suggest",
  components: {
    DropdownList,
    InputHint,
    TextInput
  },
  props: {
    hasFocus: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: "text"
    },
    name: {
      type: String,
      required: true
    },
    label: {
      type: String,
      required: true
    },
    value: {
      type: Object,
      default: null
    },
    focus: {
      type: Boolean,
      default: false
    },
    hint: String,
    error: String,
    required: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    showOptional: {
      type: Boolean,
      default: false
    },
    inverse: {
      type: Boolean,
      default: false
    },
    indexName: String,
    items: Array,
    photoField: String,
    titleField: {
      type: String,
      required: true
    },
    descField: String,
    chipField: String,
    create: {
      type: Boolean,
      required: false
    },
    isCode: {
      type: Boolean,
      default: false
    },
    isPremise: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapState({
      searchStore: state => createFromSerialized(state.algolia.searchStore)
    }),
    isAlgolia() {
      return this.indexName !== undefined;
    },
    chip() {
      if (this.isCode) {
        if (this.value && this.value.sic_code) {
          if (this.value[this.chipField || this.titleField].indexOf(":") > -1) {
            return this.value[this.chipField || this.titleField];
          }
          return this.value
            ? this.value.sic_code +
                ": " +
                this.value[this.chipField || this.titleField]
            : "";
        }
        if (this.value && this.value.code) {
          if (this.value[this.chipField || this.titleField].indexOf(":") > -1) {
            return this.value[this.chipField || this.titleField];
          }
          return this.value
            ? this.value.code +
                ": " +
                this.value[this.chipField || this.titleField]
            : "";
        }
      }
      if (this.isPremise && this.value) {
        if (this.value.description && this.value.description.length) {
          return (
            this.value.loc +
            ": " +
            this.value.address.short_address +
            " (" +
            this.value.description +
            ")"
          );
        }
        return this.value.loc + ": " + this.value.address.short_address;
      }
      return this.value ? this.value[this.chipField || this.titleField] : "";
    },
    createText() {
      if (this.create && this.query.trim().length > 0) {
        return `Create "${this.query}"`;
      }
      return "";
    }
  },
  data() {
    return {
      mouseoverItems: false,
      showOnClick: false,
      query: "",
      currentIndex: -1,
      queryItems: this.items,
      inputFocus: this.focus
    };
  },
  mounted() {
    if (this.focus) this.setFocus();
  },
  methods: {
    arrowMoved(up) {
      const { dropdown } = this.$refs;
      this.currentIndex = Math.min(
        this.isAlgolia
          ? dropdown.maxIndex
          : this.queryItems.length - (this.create ? 0 : 1),
        Math.max(-1, this.currentIndex + (up ? -1 : 1))
      );
    },
    createNewResult() {
      this.$emit("create", this.query);
      this.query = "";
      this.currentIndex = -1;
      this.showOnClick = false;
    },
    filterByQuery() {
      if (!this.isAlgolia) {
        if (this.query.length <= 0) {
          return (this.queryItems = this.items);
        }
        let keys = [this.titleField];
        if (this.descField) {
          keys.push(this.descField);
        }
        const options = { keys };
        return this.$search(this.query, this.items, options).then(
          items => (this.queryItems = items)
        );
      }
    },
    selectDropdown() {
      const { dropdown } = this.$refs;
      this.selectResult(dropdown.getHighlightedResult());
    },
    selectResult(result) {
      this.query = "";
      this.currentIndex = -1;
      this.$emit("change", result);
      this.$emit("resolve-error");

      if (this.hasFocus) {
        this.showOnClick = false;
      }
    },
    setFocus() {
      this.$refs.input.setFocus();
    },
    onFocus() {
      this.inputFocus = true;
      if (this.hasFocus) {
        this.queryItems = this.items;
        this.showOnClick = true;
      }
    },
    onBlur() {
      this.inputFocus = false;
      if (this.hasFocus && !this.mouseoverItems) {
        this.showOnClick = false;
      }
    },
    mouseover(val) {
      this.mouseoverItems = val;
    }
  },
  watch: {
    items() {
      this.filterByQuery();
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~@/style/_variables.scss";
.suggest {
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
  margin-bottom: $padding-xl;

  &__wrapper {
    display: flex;
    flex-direction: column;
    width: 100%;
    position: relative;

    .dropdown {
      position: absolute;
      bottom: 0;
      left: 0;
      transform: translateY(100%);
    }
  }

  &.hide-error {
    .error {
      display: none;
    }
  }
}
</style>
